import React from 'react';

/**
 * GovFlow Logo SVG — Marca fiel ao logotipo oficial
 * Variantes: 'full' (ícone + texto), 'icon' (apenas ícone), 'text' (apenas texto)
 */
interface GovFlowLogoProps {
  variant?: 'full' | 'icon' | 'text';
  dark?: boolean; // true = versão para fundos escuros (texto branco/claro)
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

const SIZES = {
  xs: { icon: 24, text: 14, sub: 8 },
  sm: { icon: 32, text: 18, sub: 9 },
  md: { icon: 44, text: 24, sub: 11 },
  lg: { icon: 64, text: 36, sub: 13 },
};

export const GovFlowLogo: React.FC<GovFlowLogoProps> = ({
  variant = 'full',
  dark = false,
  size = 'sm',
}) => {
  const s = SIZES[size];
  const govColor  = dark ? '#FFFFFF' : '#1B2D72';
  const flowColor = dark ? '#60A5FA' : '#1565C0';
  const teal      = '#00C4CF';
  const navyDark  = '#1B2D72';

  const icon = (
    <svg
      width={s.icon}
      height={s.icon}
      viewBox="0 0 80 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Document body */}
      <rect x="14" y="6" width="42" height="52" rx="5" fill="white" stroke={navyDark} strokeWidth="3.5" />
      {/* Folded corner */}
      <path d="M44 6 L56 18 L44 18 Z" fill={teal} stroke={navyDark} strokeWidth="2" strokeLinejoin="round" />
      <path d="M44 6 L44 18 L56 18" fill="none" stroke={navyDark} strokeWidth="2" />
      {/* Document lines */}
      <line x1="23" y1="30" x2="45" y2="30" stroke={navyDark} strokeWidth="3" strokeLinecap="round" />
      <line x1="23" y1="37" x2="45" y2="37" stroke={navyDark} strokeWidth="3" strokeLinecap="round" />
      <line x1="23" y1="44" x2="35" y2="44" stroke={teal} strokeWidth="3" strokeLinecap="round" />
      {/* Pipeline / flow line */}
      <circle cx="8" cy="54" r="4.5" fill="none" stroke={navyDark} strokeWidth="3" />
      <path d="M12.5 54 Q28 54 28 62 Q28 70 44 70" stroke={navyDark} strokeWidth="3" strokeLinecap="round" fill="none" />
      <circle cx="44" cy="70" r="4.5" fill="none" stroke={navyDark} strokeWidth="3" />
      <path d="M48.5 70 Q56 70 56 62 Q56 54 64 54" stroke={navyDark} strokeWidth="3" strokeLinecap="round" fill="none" />
      {/* Pixel dots (left) */}
      <rect x="1" y="44" width="4" height="4" rx="1" fill={teal} />
      <rect x="6" y="44" width="4" height="4" rx="1" fill={teal} opacity="0.5" />
      <rect x="1" y="49" width="4" height="4" rx="1" fill={teal} opacity="0.5" />
      {/* Checkmark badge */}
      <circle cx="68" cy="52" r="12" fill={teal} />
      <path d="M63 52 L66.5 55.5 L73 48.5" stroke="white" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const textMark = (
    <div className="flex flex-col leading-none">
      <div className="flex items-baseline">
        <span
          className="font-black tracking-tight"
          style={{ fontSize: s.text, color: govColor, fontFamily: 'Inter, system-ui, sans-serif', letterSpacing: '-0.02em' }}
        >
          Gov
        </span>
        <span
          className="font-black tracking-tight"
          style={{ fontSize: s.text, color: flowColor, fontFamily: 'Inter, system-ui, sans-serif', letterSpacing: '-0.02em' }}
        >
          Flow
        </span>
      </div>
      {size !== 'xs' && (
        <span
          className="tracking-widest uppercase font-semibold"
          style={{ fontSize: s.sub, color: dark ? 'rgba(255,255,255,0.5)' : '#64748b', letterSpacing: '0.12em', marginTop: 1 }}
        >
          Demandas Públicas
        </span>
      )}
    </div>
  );

  if (variant === 'icon') return icon;
  if (variant === 'text') return textMark;

  return (
    <div className="flex items-center gap-2.5">
      {icon}
      {textMark}
    </div>
  );
};

export default GovFlowLogo;
